import React, { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck,
  Truck,
  Star,
  ShoppingBag,
  Check,
  ChevronDown,
  Zap,
} from "lucide-react";
import { useCart } from "@/context/CartContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Focused ad landing page for Google/Meta ads.
 * - Zero nav / header (only exit is the CTA → cart)
 * - Message-match to the ad angle: German Latex + Swiss Made + Active Keeper
 * - Above-the-fold: image · name · price · size picker · Add-to-Cart
 * - Mobile-first (most ad traffic)
 * - Data-testid coverage for testing
 *
 * Routes:
 *   /panther-pro  → panther-edition
 *   /mamba-pro    → mamba-edition
 */
const CONFIG = {
  "panther-pro": {
    slug: "panther-edition",
    label: "Panther Edition",
    hero_kicker: "Für trockene Bedingungen · Robust",
    hero_headline: "Der Handschuh, der bei jedem Schuss steht.",
    hero_sub:
      "4mm German Contact Grip Latex · Breathapren Body · Negative Cut. Der ausgewogene Allrounder für Torhüter, die einen Handschuh wollen, der eine ganze Saison hält — nicht nur ein Spiel.",
    proof_line: "Getestet von Torhütern im FC Wädenswil & FC Uitikon",
  },
  "mamba-pro": {
    slug: "mamba-edition",
    label: "Green Mamba",
    hero_kicker: "Für Nässe · Maximaler Grip",
    hero_headline: "Wenn der Ball klebt, bleibt er dir.",
    hero_sub:
      "4mm German Giga Grip Latex · Breathapren Body · Silikon-Punchingzone. Der klebrigste Latex, den wir bauen können — für Regen, Nebel, Nachtspiele. Signature Green Colorway.",
    proof_line: "Getestet von Torhütern im FC Wädenswil & FC Uitikon",
  },
};

const TRUST_ICONS = [
  { icon: ShieldCheck, label: "Handmade Pro Quality" },
  { icon: Truck, label: "Gratis Versand ab CHF 80" },
  { icon: Star, label: "5.0 Google Reviews" },
];

const AD_FAQS = [
  {
    q: "Wie schnell ist die Lieferung?",
    a: "Bestellungen bis 14:00 Uhr gehen am selben Tag mit der Schweizerischen Post raus. Lieferung in 2–4 Werktagen innerhalb der Schweiz.",
  },
  {
    q: "Welche Größe brauche ich?",
    a: "Miss den Umfang deiner dominanten Hand ohne Daumen. 17–18 cm = 6, 18–19 cm = 7 (Damen/Jugend), 20–21 cm = 8, 22–23 cm = 9 (Standard Herren), 23–24 cm = 10. Im Zweifel eine Nummer kleiner — Latex dehnt sich leicht.",
  },
  {
    q: "Kann ich zurückschicken wenn's nicht passt?",
    a: "Ja, 14 Tage Rückgaberecht auf ungetragene, unbenutzte Handschuhe in Originalverpackung. Schreib uns kurz an info@4keeperz.ch.",
  },
];

const AdLandingPage = () => {
  const location = useLocation();
  const key = location.pathname.replace(/^\//, "");
  const config = CONFIG[key];
  const cart = useCart();
  const [products, setProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);

  const product = useMemo(
    () => products.find((p) => p.slug === config?.slug),
    [products, config]
  );

  useEffect(() => {
    if (!config) return;
    document.title = `${config.label} — 4keeperz | ${config.hero_kicker}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `${config.label} · ${config.hero_sub.slice(0, 155)}`
      );
    }
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, [config]);

  // Product JSON-LD for this focused page → richer ad landing signals
  useEffect(() => {
    if (!product) return;
    const scriptId = "ad-landing-schema";
    document.getElementById(scriptId)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: product.image ? `https://4keeperz.ch${product.image}` : undefined,
      brand: { "@type": "Brand", name: "4keeperz" },
      offers: {
        "@type": "Offer",
        priceCurrency: "CHF",
        price: String(product.price_chf),
        availability: "https://schema.org/InStock",
        url: `https://4keeperz.ch/${key}`,
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: "12",
      },
    });
    document.head.appendChild(script);
    return () => document.getElementById(scriptId)?.remove();
  }, [product, key]);

  if (!config) return <Navigate to="/" replace />;

  const handleBuy = () => {
    if (!product) return;
    if (!selectedSize) {
      toast.error("Bitte Größe wählen");
      return;
    }
    cart.addItem(
      { slug: product.slug, name: product.name, image: product.image, price_chf: product.price_chf },
      selectedSize
    );
    toast.success(`${product.name} · Größe ${selectedSize} in den Warenkorb`);
  };

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid={`ad-page-${key}`}>
      {/* Minimal top bar — logo only, no nav */}
      <div className="border-b border-white/5 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="ad-logo-home">
            <img src="/logo-4keeperz.png" alt="4keeperz" className="h-9" />
          </Link>
          <div className="flex items-center gap-2 font-condensed uppercase tracking-[0.2em] text-[11px] text-[#00A651]">
            <ShieldCheck className="w-3.5 h-3.5" />
            Handmade in Switzerland
          </div>
        </div>
      </div>

      {/* HERO — above the fold conversion module */}
      <section
        className="relative py-10 md:py-16"
        style={{
          background:
            "radial-gradient(ellipse 700px 500px at 80% 20%, rgba(0,166,81,0.16), transparent 60%), radial-gradient(ellipse 500px 400px at 10% 90%, rgba(0,166,81,0.08), transparent 60%), #050505",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Left: image */}
          <div className="order-1 md:order-1">
            <div className="aspect-square bg-black border border-white/10 overflow-hidden">
              {product ? (
                <img
                  src={
                    product.image?.startsWith("/")
                      ? `${BACKEND}${product.image}`
                      : product.image
                  }
                  alt={`${config.label} · Torwarthandschuh · 4keeperz`}
                  className="w-full h-full object-cover"
                  data-testid="ad-hero-img"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30">Lädt...</div>
              )}
            </div>
          </div>

          {/* Right: conversion module */}
          <div className="order-2 md:order-2 flex flex-col gap-5">
            <div className="inline-flex items-center gap-2.5 border border-[#00A651]/40 bg-[#00A651]/5 px-4 py-2 self-start">
              <span className="w-1.5 h-1.5 bg-[#00A651] rounded-full animate-pulse" />
              <span className="font-condensed uppercase tracking-[0.3em] text-[10px] text-[#00A651]">
                {config.hero_kicker}
              </span>
            </div>

            <h1 className="font-brush text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              {config.hero_headline}
            </h1>
            <p className="text-white/70 leading-relaxed">{config.hero_sub}</p>

            {/* Price + reviews */}
            <div className="flex items-baseline gap-4 pt-2">
              <span className="font-display text-4xl text-[#00A651]">
                {product ? `CHF ${product.price_chf}` : "—"}
              </span>
              <div className="flex items-center gap-1 text-white/70 text-sm">
                <div className="flex text-[#00A651]">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="font-mono text-xs">5.0</span>
              </div>
            </div>

            {/* Size picker */}
            <div className="pt-3">
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
                Größe wählen
              </div>
              <div className="flex flex-wrap gap-2">
                {["7", "8", "9", "10", "11"].map((s) => {
                  const active = selectedSize === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      className={`w-12 h-12 border font-brush text-xl transition-colors ${
                        active
                          ? "border-[#00A651] bg-[#00A651] text-black"
                          : "border-white/20 hover:border-[#00A651]/60 text-white"
                      }`}
                      data-testid={`ad-size-${s}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-white/40 mt-2 font-condensed uppercase tracking-widest">
                Größe 6 in Kürze · Alle Größen 7–11 an Lager
              </div>
            </div>

            {/* Primary CTA */}
            <button
              onClick={handleBuy}
              disabled={!product}
              className="mt-2 inline-flex items-center justify-center gap-3 bg-[#00A651] hover:bg-[#00C961] disabled:opacity-50 disabled:cursor-not-allowed text-black font-condensed uppercase tracking-[0.2em] text-[13px] py-4 px-8 transition-colors"
              data-testid="ad-buy-btn"
            >
              <ShoppingBag className="w-4 h-4" />
              Jetzt in den Warenkorb
            </button>

            <p className="text-[11px] text-white/50 font-condensed uppercase tracking-widest">
              Versand mit Schweizerischer Post · 2–4 Werktage · CHF 8 (gratis ab CHF 80)
            </p>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-white/5 bg-black">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center md:justify-between gap-6">
          {TRUST_ICONS.map((t) => (
            <div
              key={t.label}
              className="flex items-center gap-2.5 text-white/70 font-condensed uppercase tracking-widest text-[11px]"
            >
              <t.icon className="w-4 h-4 text-[#00A651]" />
              {t.label}
            </div>
          ))}
        </div>
      </section>

      {/* Why · social proof */}
      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-[#00A651] mb-3">
            Warum 4keeperz
          </div>
          <h2 className="font-brush text-3xl md:text-4xl leading-tight mb-8">
            Von Torhütern.
            <span className="text-[#00A651]"> Für Torhüter.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Zap,
                title: "German Giga Grip / Contact Grip",
                text: "4mm Premium-Latex aus Deutschland. Derselbe Latex, den Profis tragen — ohne Marken-Aufschlag.",
              },
              {
                icon: ShieldCheck,
                title: "Gebaut von einem aktiven Keeper",
                text: "Segsan Gröschel (FC Wädenswil) & Ex-Profi Ivan Bonfardin (FC Zürich, Juventus) haben jeden Nähstich getestet.",
              },
              {
                icon: Check,
                title: "Direktverkauf ohne Zwischenhändler",
                text: "Kein Ochsner-Aufpreis, kein Nike-Marketing. Fairer Preis für ehrliches Handwerk.",
              },
            ].map((c) => (
              <div
                key={c.title}
                className="border border-white/10 bg-[#0A0A0A] p-5"
              >
                <div className="w-8 h-8 border border-[#00A651] flex items-center justify-center mb-3">
                  <c.icon className="w-4 h-4 text-[#00A651]" />
                </div>
                <h3 className="font-condensed uppercase tracking-widest text-sm mb-2">
                  {c.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3 border border-[#00A651]/20 bg-[#00A651]/5 p-4">
            <Star className="w-5 h-5 text-[#00A651] fill-current flex-shrink-0" />
            <p className="text-sm text-white/80">
              <strong>{config.proof_line}</strong> · Partner-Shop KS-Sport AG
              Siebnen (jetzt auch offline erhältlich).
            </p>
          </div>
        </div>
      </section>

      {/* Ad FAQ — 3 direct answers */}
      <section className="py-14 md:py-20 bg-black border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-[#00A651] mb-3">
            Kurze Antworten
          </div>
          <h2 className="font-brush text-3xl md:text-4xl mb-8">Bevor du bestellst.</h2>
          <div className="border border-white/10 bg-[#0A0A0A]">
            {AD_FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <button
                  key={i}
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full text-left border-b border-white/5 last:border-b-0 p-5 flex flex-col gap-3"
                  data-testid={`ad-faq-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-condensed uppercase tracking-widest text-sm text-white">
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-white/40 flex-shrink-0 transition-transform ${
                        open ? "rotate-180 text-[#00A651]" : ""
                      }`}
                    />
                  </div>
                  {open && (
                    <p className="text-sm text-white/70 leading-relaxed">{f.a}</p>
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-8 mx-auto flex items-center gap-3 border border-white/25 hover:border-[#00A651] hover:text-[#00A651] text-white font-condensed uppercase tracking-[0.2em] text-[12px] px-6 py-3 transition-colors"
            data-testid="ad-back-to-top"
          >
            <ShoppingBag className="w-4 h-4" />
            Zurück zur Bestellung
          </button>
        </div>
      </section>

      {/* Minimal footer — only legal + trust */}
      <footer className="border-t border-white/5 py-6 px-6">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-white/40 font-condensed uppercase tracking-widest">
          <div>© 4keeperz · Twirrenstrasse 150, 8733 Eschenbach SG</div>
          <div className="flex gap-4">
            <Link to="/legal/impressum" className="hover:text-white/70">
              Impressum
            </Link>
            <Link to="/legal/datenschutz" className="hover:text-white/70">
              Datenschutz
            </Link>
            <Link to="/legal/agb" className="hover:text-white/70">
              AGB
            </Link>
            <Link to="/" className="hover:text-[#00A651]" data-testid="ad-to-fullshop">
              Voller Shop →
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdLandingPage;
